import { ErrorPanel, Refused } from '@cwos/ui-admin';
import { httpStatusOf, refusedPermissionOf } from '../features/LoadFailure.js';

/**
 * ===========================================================================
 * THE MUTATION TWIN OF `LoadFailure` — PH-UX-003.
 * ===========================================================================
 *
 * `LoadFailure.tsx` already drew the READ-side line: a failed query renders
 * one of three true sentences (signed-out / not-allowed / unreachable) rather
 * than a fourth, false one built from `err instanceof Error ? err.message :
 * '<friendly fallback>'`. That ternary is dead code at 89 sites in this app —
 * both transports this app throws from (`@cwos/api-client`'s `ApiError`
 * family and the plain `Error` `lib/api.ts` decorates with `.status`) are
 * `Error` subclasses, so the left branch always wins and the friendly
 * right-hand sentence a screen's author wrote never renders. What DOES render
 * is whatever `apps/api/src/server.ts`'s generic handler put in `message` —
 * every sub-500 body's `error.message` verbatim, with no
 * `schemaErrorFormatter` registered, so a 400 can be Fastify's own
 * `body/… must have required property …` — and every 5xx's specific cause is
 * logged and then replaced with the single sentence `An unexpected error
 * occurred.`, discarded rather than shown.
 *
 * `mutationFailureMessage` is the write-side answer, built the same way
 * `LoadFailure` was: structurally, off `httpStatusOf`/`refusedPermissionOf`
 * (which already read both error shapes without `instanceof`), never off a
 * regex against `message`. The two statuses whose `message` this app's own
 * routes compose ON PURPOSE as a customer sentence — 409 (a conflict a route
 * wrote prose for, e.g. "an import for this integration is already running")
 * and 422 (the same, for a semantic validation failure) — are shown verbatim;
 * every other sub-500 message is Fastify's or an unmapped domain sentence and
 * is replaced by the caller's OWN fallback, which is the sentence every one of
 * the 89 sites already wrote and could never reach.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS DOES NOT FIX ON ITS OWN
 * ---------------------------------------------------------------------------
 * This lane owns `apps/admin/src/lib/**` error handling plus the
 * `sessions`/`insights` feature directories — none of which contains any of
 * the 89 `instanceof Error ? … : …` sites (they are all in `settings/`,
 * `people/`, `time/`, `reporting/`, `compliance/`, `right-now/` and
 * `signin/`, every one a live lane or an explicitly fenced route this branch
 * does not touch). `InsightDetail.tsx` is this lane's representative
 * application of the pattern below. The remaining 88 sites, the
 * `schemaErrorFormatter` registration, and removing the `/already running/i`
 * regex at `RecordsSettings.tsx:259` all need edits outside this lane's
 * ownership; see `isSyncAlreadyRunningConflict` below for the drop-in,
 * structural replacement for that one regex, ready for whoever owns that
 * file.
 */

/**
 * The `x-request-id` an error carries, or `null` when it never reached a
 * server that could echo one back.
 *
 * STRUCTURAL, NOT `instanceof` — the same reasoning as `LoadFailure.httpStatusOf`:
 * two unrelated shapes throw into this app (`@cwos/api-client`'s `ApiError`
 * family, and the plain `Error` `lib/api.ts` decorates with `.requestId`), and
 * neither is `instanceof` the other.
 */
export function requestIdOf(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const id = (err as { requestId?: unknown }).requestId;
  return typeof id === 'string' && id.trim() !== '' ? id.trim() : null;
}

function serverMessageOf(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const message = (err as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() !== '' ? message.trim() : null;
}

/**
 * The one sentence an operator should read after a failed mutation — Save,
 * Connect, Sync, Approve, Dismiss, whatever the button was.
 *
 *   401 → the session is gone; retrying repeats it, and the remedy is signing
 *         in again, not patience.
 *   403 → a settled refusal, naming the permission when the server named one
 *         (mirrors `LoadFailure.refusalMessage`).
 *   409 / 422 → the server's own `message`. These are the two statuses this
 *         app's routes use for a PRODUCT sentence composed on purpose (see
 *         `apps/api/src/domains/integrations/routes.ts`'s `StateTransitionError`,
 *         `SyncAlreadyRunningError`, `CredentialValidationFailedError`
 *         handlers) rather than engineering prose or Fastify's schema text.
 *   everything else (4xx validation text nobody mapped, 5xx, network) → the
 *         caller's own fallback — the sentence every one of the 89 sites
 *         already wrote and could never reach — with the request id offered
 *         for support when the transport attached one.
 */
export function mutationFailureMessage(err: unknown, fallback: string): string {
  const status = httpStatusOf(err);
  if (status === 401) {
    return 'Your sign-in has ended, so this was not saved. Sign in again — nothing about your access has changed.';
  }
  if (status === 403) {
    const permission = refusedPermissionOf(err);
    return permission === null
      ? 'You do not have permission to do that. Ask whoever administers your organization if you were expecting to.'
      : `You do not have permission to do that. It needs the ${permission} permission, which whoever administers your organization can grant.`;
  }
  if (status === 409 || status === 422) {
    return serverMessageOf(err) ?? fallback;
  }
  const requestId = requestIdOf(err);
  return requestId === null ? fallback : `${fallback} Quote request ${requestId} to support.`;
}

/**
 * A STRUCTURAL REPLACEMENT for `RecordsSettings.tsx:259`'s
 * `err instanceof Error && /already running/i.test(err.message)`.
 *
 * That regex is a UI decision branching on the TEXT of a server message — the
 * sharpest instance of PH-UX-003, because a wording pass on
 * `SyncAlreadyRunningError`'s sentence
 * (`apps/api/src/domains/integrations/service.ts:143`) breaks it silently.
 * The route already sends the structured fact the regex is trying to
 * approximate: a 409 whose body carries `runId`/`startedAt`
 * (`apps/api/src/domains/integrations/routes.ts`'s `SyncAlreadyRunningError`
 * handler) — no OTHER 409 this app throws attaches a `runId`. This checks
 * those fields, never `.message`, and keeps answering correctly if the
 * sentence changes, is translated, or gains a comma.
 *
 * Not wired into `RecordsSettings.tsx` from here: that file is `settings/`,
 * a live lane outside this branch's ownership (see the file docblock). This
 * is the tested, drop-in replacement for that one line, for whoever owns it
 * next: `err instanceof Error && /already running/i.test(err.message)`
 * becomes `isSyncAlreadyRunningConflict(err)`.
 */
export function isSyncAlreadyRunningConflict(err: unknown): boolean {
  if (httpStatusOf(err) !== 409) return false;
  if (typeof err !== 'object' || err === null) return false;
  const body = (err as { body?: unknown }).body;
  if (typeof body !== 'object' || body === null) return false;
  return typeof (body as { runId?: unknown }).runId === 'string';
}

export interface MutationFailureProps {
  /** The rejected mutation's error. */
  error: unknown;
  /**
   * The screen's own sentence for the case the server gave no useful reason —
   * previously the dead right-hand side of an `instanceof Error ? … : …`.
   */
  fallback: string;
  /** Override the refusal heading where a screen can be more specific. */
  refusedHeading?: string;
  /** Offered only where retrying is a real option — never for a 403. */
  onRetry?: () => void;
}

/**
 * Renders `mutationFailureMessage` in the right shape: `Refused` (no retry,
 * ever) for a settled 403, `ErrorPanel` for everything else.
 */
export function MutationFailure({
  error,
  fallback,
  refusedHeading,
  onRetry,
}: MutationFailureProps) {
  if (httpStatusOf(error) === 403) {
    return <Refused heading={refusedHeading} message={mutationFailureMessage(error, fallback)} />;
  }
  return <ErrorPanel message={mutationFailureMessage(error, fallback)} onRetry={onRetry} />;
}
